import * as _ from 'lodash';

import { Price } from 'services';
import { Image, ImageResolution, productImagePlaceholder, getOrderAreaImageUrl } from './image';
import { formatDeliveryDateToText } from '../b2b-order/helpers/format-delivery-date';

// TODO: [ddd-cleanup @replenishment, @content] - duplicate required constructs in this file into your domain directory.

export function getTranslationKeyForAttribute(attributeGroup: AttributeGroup, attribute: string) {
    return _.toUpper(`${attributeGroup}.${attribute}`);
}

export function getArticlePrice(products: { price?: Price }[]): Price {
    const prices = _.reduce(
        products,
        (allPrices, { price }) => {
            const buildPrices = (priceProperty: keyof Price) => [
                ...allPrices[priceProperty],
                ...(price[priceProperty] ? [price[priceProperty]] : [])
            ];

            return price
                ? {
                      discountedPurchasePrice: buildPrices('discountedPurchasePrice'),
                      purchasePrice: buildPrices('purchasePrice'),
                      recommendedRetailPrice: buildPrices('recommendedRetailPrice'),
                      currency: buildPrices('currency'),
                      rrpCurrency: buildPrices('rrpCurrency')
                  }
                : allPrices;
        },
        {
            discountedPurchasePrice: [],
            purchasePrice: [],
            recommendedRetailPrice: [],
            currency: [],
            rrpCurrency: []
        }
    );

    const getMinPrice = (priceArray: number[]) => (_.isEmpty(priceArray) ? null : _.min(priceArray));

    const currency = _.toUpper(_.isEmpty(prices.currency) ? 'EUR' : prices.currency[0]);

    // Use discount from the size with the lowest discounted purchase price
    const minDiscountedPriceEntry = _.minBy(
        products,
        ({ price }) => price?.discountedPurchasePrice ?? Number.POSITIVE_INFINITY
    );
    const minDiscountedPrice = minDiscountedPriceEntry?.price;
    const discountPercentage =
        minDiscountedPrice?.purchasePrice && minDiscountedPrice?.discountedPurchasePrice
            ? ((minDiscountedPrice.purchasePrice - minDiscountedPrice.discountedPurchasePrice) /
                  minDiscountedPrice.purchasePrice) *
              100
            : null;

    return {
        discountedPurchasePrice: getMinPrice(prices.discountedPurchasePrice),
        purchasePrice: getMinPrice(prices.purchasePrice),
        recommendedRetailPrice: getMinPrice(prices.recommendedRetailPrice),
        currency,
        rrpCurrency: _.toUpper(_.isEmpty(prices.rrpCurrency) ? currency : prices.rrpCurrency[0]),
        discount: discountPercentage && discountPercentage > 0 ? Math.round(discountPercentage) : null
    };
}

export function getArticleMainImageUrl(products: { media?: { images: Partial<Image>[] } }[]): string {
    const product = _.find(products, ({ media }) => !_.isEmpty(_.get(media, 'images')));
    const mainImage = _.get(product, 'media.images') ? _.find(product.media.images, { isMainImage: true }) : {};

    return _.get(mainImage, '_id')
        ? getOrderAreaImageUrl(mainImage._id, ImageResolution.Thumb)
        : productImagePlaceholder;
}

export function getLightboxOrderedMetadata(product) {
    return _.pick(product, lightboxOrderedProperties);
}
export function getLightboxUnorderedMetadata(product) {
    const productWithoutOrderedProperties: any = _.omit(product, lightboxOrderedProperties);
    return {
        ...productWithoutOrderedProperties,
        manufacturerAttributes: {
            ...productWithoutOrderedProperties.manufacturerAttributes,
            ...(_.has(product, 'manufacturerAttributes.deliveryDates')
                ? {
                      deliveryDates: product.manufacturerAttributes.deliveryDates
                          .map(formatDeliveryDateToText)
                          .join(', ')
                  }
                : null)
        }
    };
}

export function getArticleHash({ styleGroupId, colorGroupId }: { styleGroupId: string; colorGroupId: string }): string {
    return `${styleGroupId}#${colorGroupId}`;
}

export function isSameArticle(
    firstArticle: { styleGroupId: string; colorGroupId: string },
    secondArticle: { styleGroupId: string; colorGroupId: string }
): boolean {
    return !secondArticle ? false : getArticleHash(firstArticle) === getArticleHash(secondArticle);
}

const lightboxOrderedProperties = [
    'articleNumber',
    'gtin',
    'manufacturerAttributes.name',
    'manufacturerAttributes.color',
    'manufacturerAttributes.category',
    'manufacturerAttributes.size',
    'manufacturerAttributes.targetGroup',
    'manufacturerAttributes.material',
    'manufacturerAttributes.description',
    'manufacturerAttributes.price',
    'manufacturerAttributes.currency',
    'manufacturerAttributes.certificates',
    'manufacturerAttributes.subcategory'
];

export enum AttributeGroup {
    ManufacturerAttributes = 'manufacturerAttributes',
    FcAttributes = 'fcAttributes',
    SUSTAINABILITYATTRIBUTES = 'sustainabilityAttributes'
}
